<template>
  <div class="breadcrumb" ref="breadcrumbRef">
    <!-- Home icon -->
    <b-icon
      icon="house-fill"
      scale="1.25"
      shift-v="1.25"
      aria-hidden="true"
      class="mr-1"
    />

    <!-- Separator and intermediate elements -->
    <template v-for="(parent, index) in filteredParents">
      <span
        v-if="index !== filteredParents.length - 1"
        :key="'parent-' + index"
        class="breadcrumb-item"
      >
        {{ $tc(parent.title, 2) }}
      </span>
      <!-- Last item -->
      <span
        v-else
        :key="'last-parent-' + index"
        class="breadcrumb-item"
        @mouseenter="openDropdown"
        @mouseleave="startCloseDropdownTimer"
      >
        {{ $tc(parent.title, 1) }}
        <!-- Dropdown list -->
        <ul
          v-if="showDropdown && hasValidBrothers"
          class="breadcrumb-menu"
          @mouseenter="cancelCloseDropdownTimer"
          @mouseleave="startCloseDropdownTimer"
        >
          <li
            v-for="(brother, idx) in filteredBrothers"
            :key="'brother-' + idx"
            class="breadcrumb-menu-item"
          >
            <router-link :to="resolveMenuItemTo(brother)">
              {{ $tc(brother.title, 1) }}
            </router-link>
          </li>
        </ul>
      </span>
    </template>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import menu from "@/navigation/horizontal/menu";
import { resolveNavItemTo } from "@core/layouts/utils";
import { hasBauComplementsQueryFlag } from "@/utils/bauFormQuery";

const ROUTE_FALLBACK = {
  planningCreate: "planning",
  planningEdit: "planning",
  policyExceptionCreate: "policyExceptions",
  policyExceptionEdit: "policyExceptions",
};

const getMenuItemRouteName = (item) => {
  if (!item || !item.route) {
    return null;
  }

  if (typeof item.route === "string") {
    return item.route;
  }

  return item.route.name || null;
};

const isBauComplementsMenuItem = (item) => {
  if (!item || typeof item.route === "string") {
    return false;
  }

  return hasBauComplementsQueryFlag(item.route.query || {});
};

const doesMenuItemMatchCurrentRoute = ({ item, routeName, routeQuery }) => {
  const menuItemRouteName = getMenuItemRouteName(item);
  if (!menuItemRouteName || menuItemRouteName !== routeName) {
    return false;
  }

  const isCurrentRouteBauForm = hasBauComplementsQueryFlag(routeQuery);
  if (!isCurrentRouteBauForm) {
    return !isBauComplementsMenuItem(item);
  }

  return isBauComplementsMenuItem(item);
};

const findRouteHierarchy = ({ items, routeName, routeQuery, parents = [] }) => {
  for (const item of items) {
    if (doesMenuItemMatchCurrentRoute({ item, routeName, routeQuery })) {
      return {
        parents: [...parents, item],
        brothers: items.filter((brotherItem) => brotherItem !== item),
      };
    }

    if (item.children) {
      const result = findRouteHierarchy({
        items: item.children,
        routeName,
        routeQuery,
        parents: [...parents, item],
      });
      if (result) {
        return result;
      }
    }
  }

  return null;
};

export default {
  setup(_, ctx) {
    const routeName = ctx.root.$route.name;
    const routeQuery = ctx.root.$route.query || {};
    const resolveMenuItemTo = resolveNavItemTo;
    const showDropdown = ref(false);
    const closeTimer = ref(null);
    const resolvedRouteName = ROUTE_FALLBACK[routeName] || routeName;
    const routeHierarchy =
      findRouteHierarchy({
        items: menu,
        routeName,
        routeQuery,
      }) ||
      findRouteHierarchy({
        items: menu,
        routeName: resolvedRouteName,
        routeQuery,
      });

    const parents = computed(() => routeHierarchy?.parents || []);
    const brothers = computed(() => routeHierarchy?.brothers || []);

    // Valid parents and brothers with $can validation
    const filteredParents = computed(() =>
      parents.value.filter((parent) => ctx.root.$can(parent.action, parent.resource))
    );
    const filteredBrothers = computed(() =>
      brothers.value.filter(
        (brother) =>
          resolveMenuItemTo(brother) &&
          ctx.root.$can(brother.action, brother.resource)
      )
    );

    const hasValidBrothers = computed(() => filteredBrothers.value.length > 0);

    const openDropdown = () => {
      showDropdown.value = true;
      clearTimeout(closeTimer.value);
    };

    const startCloseDropdownTimer = () => {
      closeTimer.value = setTimeout(() => {
        showDropdown.value = false;
      }, 600);
    };

    const cancelCloseDropdownTimer = () => {
      clearTimeout(closeTimer.value);
    };

    return {
      filteredParents,
      filteredBrothers,
      hasValidBrothers,
      resolveMenuItemTo,
      showDropdown,
      openDropdown,
      startCloseDropdownTimer,
      cancelCloseDropdownTimer,
    };
  },
};
</script>

<style scoped>
.breadcrumb {
  display: flex;
  align-items: center;
  color: #1b3d5e;
  padding: 0.3rem 0rem 0.3rem 2rem;
  width: fit-content;

  a {
    display: block;
    width: 100%;
  }
}

.breadcrumb-item {
  cursor: pointer;
}

.separator {
  display: flex;
  align-items: center;
}

.breadcrumb-menu {
  position: absolute;
  background: #fff;
  list-style: none;
  padding: 0.5rem 0rem;
  margin-left: 1rem;
  z-index: 5;
  border: 1px solid var(--third-light) !important;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);

  li {
    color: #1b3d5e;
    a {
      padding-block: 4px;
      padding-inline: 16px;
    }
  }

  &-item a {
    color: white;
  }
}

.dropdown-item {
  margin: 0.25rem 0;
}

.breadcrumb-menu-item:hover {
  background-color:  var(--second-dark) !important;
  a {
    color: #fff;
  }
}

.dark-layout .breadcrumb {
  color: #fff;
}

.dark-layout .breadcrumb-menu {
  background-color:  var(--second-dark) !important;
  border: 1px solid var(--third-dark) !important;
}

.dark-layout .breadcrumb-menu-item:hover {
  background-color: #1b3d5e;
}
</style>
